import { Box, Flex, Spinner, useToast, VStack } from "@chakra-ui/react";
import { Navigate, useNavigate, useParams } from "react-router";

import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import {
  useSavedProfile,
  useSavedProfileSigner,
  useUpdateSavedProfileSigner,
} from "@equidefi/portals/hooks/useSavedProfiles";

import { ProfileNavButton, VaultContainer } from "@/pages/app/vault/v2/components";
import { EditCard, SignerForm } from "../components";

const SavedProfileSignerEdit = () => {
  useDocumentTitle(["Signer", "Saved Profile"]);

  const { id, signerId } = useParams<{
    id: string;
    signerId: string;
  }>();

  const navigate = useNavigate();
  const toast = useToast();

  const backPath = `/vault/profile/saved-profiles/${id}`;

  const {
    data: profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
    refetch: refetchProfile,
  } = useSavedProfile(id ?? "");

  const {
    data: signer,
    isLoading: isSignerLoading,
    isError: isSignerError,
    refetch: refetchSigner,
  } = useSavedProfileSigner(id ?? "", signerId ?? "");

  const { mutateAsync: updateSavedProfileSigner } = useUpdateSavedProfileSigner(id ?? "", signerId ?? "");

  if (!id || !signerId) {
    return <Navigate to="/vault/profile/saved-profiles" replace />;
  }

  if (isProfileLoading || isSignerLoading) {
    return (
      <VaultContainer>
        <Flex justify="center" py="10">
          <Spinner />
        </Flex>
      </VaultContainer>
    );
  }

  if (isProfileError || isSignerError) {
    throw new Error("Failed to load saved profile signer");
  }

  if (!profile) {
    return <Navigate to="/vault/profile/saved-profiles" replace />;
  }

  if (!signer) {
    return <Navigate to={backPath} replace />;
  }

  const handleSave = async (values: any) => {
    await updateSavedProfileSigner(values);
    await refetchSigner();
    await refetchProfile();

    toast({
      status: "success",
      description: "Signer updated successfully.",
    });

    navigate(backPath);
  };

  return (
    <VaultContainer>
      <VStack align="stretch" spacing="4" w="full">
        <ProfileNavButton to={backPath} layout="rtl">
          Back To Saved Profile
        </ProfileNavButton>

        <EditCard title="Signer" description="Update signer information for this profile." />

        <Box
          bg="white"
          borderRadius="lg"
          p="4"
          boxShadow="0 1px 2px rgba(0,0,0,0.2)"
          border="1px solid"
          borderColor="gray.100">
          <SignerForm profile={profile} signer={signer} onSave={handleSave} />
        </Box>
      </VStack>
    </VaultContainer>
  );
};

export default SavedProfileSignerEdit;
